/**
 * Mobile sticky Add to Cart.
 *
 * Shows the bar once the real Add to Cart button has scrolled out of view, and mirrors
 * the live price and variant title into it. The bar's own button submits the page's form
 * via its `form` attribute, so nothing here touches cart state — this file only decides
 * when the bar is visible and what it says.
 */
class BeeulStickyAtc extends HTMLElement {
  connectedCallback() {
    this.priceNode = this.querySelector('[data-price]');
    this.variantNode = this.querySelector('[data-variant]');
    this.button = this.querySelector('button');

    this.source = document.getElementById(this.dataset.priceSource);
    this.target = document.querySelector(this.dataset.observe);

    this.watchVisibility();
    this.watchPrice();
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.priceObserver?.disconnect();
    document.body.classList.remove('beeul-sticky-atc-visible');
  }

  /**
   * The bar is the buy button's understudy: on stage only while the real one is off it.
   * Without IntersectionObserver the bar stays hidden rather than covering the page.
   */
  watchVisibility() {
    if (!this.target || !('IntersectionObserver' in window)) return;

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { rootMargin: '0px 0px -80% 0px' }
    );
    this.visibilityObserver.observe(this.target);
  }

  toggle(show) {
    if (show === this.shown) return;
    this.shown = show;
    this.hidden = !show;
    document.body.classList.toggle('beeul-sticky-atc-visible', show);
  }

  /**
   * Sense re-renders the price node on variant change rather than dispatching an event,
   * so the bar follows the DOM it already trusts instead of re-deriving the variant.
   */
  watchPrice() {
    if (!this.source || !('MutationObserver' in window)) return;

    this.sync();
    this.priceObserver = new MutationObserver(() => this.sync());
    this.priceObserver.observe(this.source, { childList: true, subtree: true, characterData: true });
  }

  sync() {
    if (this.priceNode && this.source) {
      // The regular price is the one the customer pays; the compare-at and the
      // visually-hidden labels beside it are noise at this size.
      const price =
        this.source.querySelector('.price-item--sale, .price-item--regular') || this.source;
      this.priceNode.textContent = price.textContent.trim();
    }

    const realButton = this.target;
    if (realButton && this.button) {
      this.button.disabled = realButton.disabled;
      // The real button wraps its label in a span and carries a spinner beside it.
      const label = realButton.querySelector('span');
      // A custom label (e.g. "Buy Now") wins while the variant is buyable — that's the
      // one state a merchant actually sets it for. Sold-out still mirrors the real
      // button's own text so the bar never claims something purchasable isn't.
      const customLabel = this.dataset.label;
      if (realButton.disabled || !customLabel) {
        if (label) this.button.textContent = label.textContent.trim();
      } else {
        this.button.textContent = customLabel;
      }
    }

    if (this.variantNode) {
      const selected = document.querySelector(
        '.product-form__input input[type="radio"]:checked, .product-form__input select option:checked'
      );
      if (selected) {
        this.variantNode.textContent = (selected.value || selected.textContent).trim();
      }
    }
  }
}

if (!customElements.get('beeul-sticky-atc')) {
  customElements.define('beeul-sticky-atc', BeeulStickyAtc);
}
